<template>
  <v-app dark class="tech-app-wrapper">
    <div class="circuit-overlay"></div>
    <div class="scanlines"></div>

    <section id="registro-nuevo-dominio" class="content-relative">
      
      <div class="hero-section-tech">
        <div class="hero-tech-content text-center">
          <v-icon size="80" color="cyan accent-3" class="mb-4 glow-icon spin-slow-reverse">mdi-hexagon-multiple</v-icon>
          <h1 class="text-h3 font-weight-black white--text neon-text tracking-widest mb-2">
            REGISTRO DE NUEVO DOMINIO
          </h1>
          <h2 class="text-h5 cyan--text text--lighten-3 mono-font">
            >> REGISTRANTES NACIONALES_
          </h2>
          <div class="scan-line-hero"></div>
          <p class="white--text mt-4 text-body-1" style="max-width: 800px; margin: 0 auto; opacity: 0.8;">
            Siga los pasos para registrar su nuevo dominio .ni. Use las pestañas o los botones para navegar.
          </p>
        </div>
      </div>

      <v-container class="pa-4 relative z-index-2" style="max-width: 1400px; margin-top: -40px;">
        
        <v-card class="tech-card rounded-lg overflow-hidden elevation-24">
          
          <div class="progress-tech-container px-4 py-6 border-bottom-neon bg-black-transparent">
            <div class="d-none d-md-block relative mb-6 mx-10">
              <div class="progress-line-base"></div>
              <div class="progress-line-active" :style="{ width: progressWidth }"></div>
            </div>

            <div class="d-flex justify-space-between align-start overflow-x-auto custom-scrollbar pb-2">
              <div 
                v-for="(step, index) in steps" 
                :key="index" 
                class="step-tech-node d-flex flex-column align-center px-2"
                :class="{
                  'active': currentStepIndex >= index,
                  'current': currentStepIndex === index
                }"
                style="min-width: 100px; cursor: pointer;"
                @click="goToStep(index)"
              >
                <div class="node-circle mb-2">
                  <v-icon x-small v-if="currentStepIndex > index" color="black">mdi-check</v-icon>
                  <span v-else class="node-number">{{ index + 1 }}</span>
                </div>
                <div class="node-label mono-font caption font-weight-bold text-center">
                  {{ step.shortTitle || step.title }}
                </div>
              </div>
            </div>
          </div>

          <v-tabs-items v-model="currentStep" class="transparent">
            
            <v-tab-item value="tab-0">
              <v-card flat color="transparent">
                <v-card-text class="pa-8">
                  <div class="text-center mb-8">
                    <v-icon size="80" color="cyan accent-3" class="mb-4 glow-icon">mdi-magnify</v-icon>
                    <h2 class="text-h4 white--text font-weight-bold neon-text">Verificar Disponibilidad</h2>
                    <p class="cyan--text text--lighten-2 mono-font">>> Compruebe si el dominio está disponible</p>
                  </div>

                  <v-row>
                    <v-col cols="12" md="8" class="mx-auto">
                      <div class="console-box pa-6 rounded border-cyan mb-6">
                        <p class="white--text text-body-1 mb-4">
                          Antes de iniciar el proceso de registro, verifique si el nombre de dominio está disponible.
                        </p>
                        
                        <v-card outlined color="rgba(0, 229, 255, 0.1)" class="border-cyan mb-4">
                          <v-card-text class="text-center">
                            <v-icon class="mb-2" color="cyan accent-3">mdi-web</v-icon>
                            <p class="white--text body-1 font-weight-bold mb-2">
                              Acceda al sistema Whois:
                            </p>
                            <a 
                              href="https://www.nic.ni" 
                              target="_blank" 
                              class="text-h6 cyan--text text--accent-3 text-decoration-none font-weight-bold tech-link"
                            >
                              https://www.nic.ni
                            </a>
                          </v-card-text>
                        </v-card>

                        <div class="mb-6">
                          <h4 class="text-h6 font-weight-bold mb-3 white--text mono-font">Alternativa por correo:</h4>
                          <v-card outlined color="rgba(0,0,0,0.3)" class="border-blue pa-4">
                            <div class="d-flex align-center">
                              <v-icon class="mr-3" color="blue accent-3">mdi-email</v-icon>
                              <div>
                                <p class="grey--text body-2 mb-1">También puede escribir a:</p>
                                <a 
                                  href="mailto:info@nic.uni.edu.ni" 
                                  class="text-h6 blue--text text--accent-3 text-decoration-none font-weight-bold tech-link"
                                >
                                  info@nic.uni.edu.ni
                                </a>
                              </div>
                            </div>
                          </v-card>
                        </div>

                        <div class="mt-6">
                          <h4 class="text-h6 font-weight-bold mb-3 white--text mono-font">Ejemplos de dominios a verificar:</h4>
                          <div class="d-flex flex-wrap justify-center">
                            <v-chip outlined color="cyan accent-3" class="ma-1 mono-font">empresa.com.ni</v-chip>
                            <v-chip outlined color="green accent-3" class="ma-1 mono-font">institucion.edu.ni</v-chip>
                            <v-chip outlined color="orange accent-3" class="ma-1 mono-font">organizacion.org.ni</v-chip>
                          </div>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <v-tab-item value="tab-1">
              <v-card flat color="transparent">
                <v-card-text class="pa-8">
                  <div class="text-center mb-8">
                    <v-icon size="80" color="cyan accent-3" class="mb-4 glow-icon">mdi-file-download</v-icon>
                    <h2 class="text-h4 white--text font-weight-bold neon-text">Descargar Documentos</h2>
                    <p class="cyan--text text--lighten-2 mono-font">>> Plantillas y cartas de compromiso</p>
                  </div>

                  <v-row>
                    <v-col cols="12" md="8" class="mx-auto">
                      <div class="console-box pa-6 rounded border-cyan mb-6">
                        <p class="white--text text-body-1 mb-4">
                          Descargue la plantilla de Registro de Dominio y la carta de compromiso correspondientes.
                        </p>

                        <v-card class="mb-4" outlined color="rgba(0, 229, 255, 0.1)" style="border: 1px solid cyan;">
                          <v-card-text class="text-center">
                            <v-icon class="mb-2" color="cyan accent-3">mdi-link</v-icon>
                            <p class="white--text body-1 font-weight-bold mb-2">
                              Enlace para descargar documentos:
                            </p>
                            <a 
                              href="https://www.nic.ni/PasosDom" 
                              target="_blank" 
                              class="text-h6 cyan--text text--accent-3 text-decoration-none font-weight-bold tech-link"
                            >
                              https://www.nic.ni/PasosDom
                            </a>
                          </v-card-text>
                        </v-card>

                        <div class="mb-6">
                          <h4 class="text-h6 font-weight-bold mb-3 white--text mono-font">Tipos de documentos según su caso:</h4>
                          <v-list dense color="transparent" dark>
                            <v-list-item class="hover-glow-item mb-2 rounded">
                              <v-list-item-icon>
                                <v-icon color="green accent-3">mdi-check-circle-outline</v-icon>
                              </v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title class="font-weight-bold cyan--text">Registrante nacional o registrador autorizado</v-list-item-title>
                                <v-list-item-subtitle class="grey--text">Documentación específica para cada categoría</v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>
                            <v-list-item class="hover-glow-item mb-2 rounded">
                              <v-list-item-icon>
                                <v-icon color="green accent-3">mdi-check-circle-outline</v-icon>
                              </v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title class="font-weight-bold cyan--text">Persona jurídica o persona natural</v-list-item-title>
                                <v-list-item-subtitle class="grey--text">Formularios diferentes según el tipo de solicitante</v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </div>

                        <v-alert border="left" colored-border color="orange accent-3" class="mb-4 black-glass">
                          <div class="d-flex align-center">
                            <v-icon left color="orange accent-3">mdi-alert-outline</v-icon>
                            <div class="white--text">
                              <strong>Importante:</strong> Asegúrese de elegir y completar los documentos correctos para su tipo de solicitante.
                            </div>
                          </div>
                        </v-alert>

                        <div class="mt-6">
                          <v-alert
                            class="text-center black-glass border-neon"
                            border="left"
                            elevation="2"
                            colored-border
                            color="cyan accent-3"
                          >
                            <template v-slot:prepend>
                              <v-icon color="cyan accent-3" class="spin-slow mr-4" large>
                                mdi-clock-outline
                              </v-icon>
                            </template>
                            <strong class="white--text">Horario de Atención:</strong><br>
                            <span class="grey--text">Lunes a Viernes de 8:00 AM a 5:00 PM</span>
                          </v-alert>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <v-tab-item value="tab-2">
              <v-card flat color="transparent">
                <v-card-text class="pa-8">
                  <div class="text-center mb-8">
                    <v-icon size="80" color="cyan accent-3" class="mb-4 glow-icon">mdi-file-document-edit-outline</v-icon>
                    <h2 class="text-h4 white--text font-weight-bold neon-text">Complete y Envíe Documentos</h2>
                    <p class="cyan--text text--lighten-2 mono-font">>> Preparación de formularios</p>
                  </div>

                  <v-row>
                    <v-col cols="12" md="10" class="mx-auto">
                      <div class="console-box pa-6 rounded border-cyan mb-6">
                        <div class="mb-6">
                          <h4 class="text-h6 font-weight-bold mb-3 cyan--text mono-font">
                            <v-icon left color="cyan">mdi-clipboard-check-outline</v-icon>
                            Requisitos de Completado
                          </h4>
                          
                          <v-list color="transparent" dark>
                            <v-list-item>
                              <v-list-item-icon><v-icon color="green accent-3">mdi-check</v-icon></v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title class="font-weight-bold white--text">Información veraz</v-list-item-title>
                                <v-list-item-subtitle class="grey--text">Asegúrese de que la información proporcionada en la plantilla sea veraz</v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>
                            <v-list-item>
                              <v-list-item-icon><v-icon color="green accent-3">mdi-check</v-icon></v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title class="font-weight-bold white--text">Firma obligatoria</v-list-item-title>
                                <v-list-item-subtitle class="grey--text">La carta de compromiso debe estar firmada</v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>
                            <v-list-item>
                              <v-list-item-icon><v-icon color="green accent-3">mdi-check</v-icon></v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title class="font-weight-bold white--text">Requisito para contribuyentes</v-list-item-title>
                                <v-list-item-subtitle class="purple--text text--accent-2 font-weight-bold">
                                  Si es contribuyente, la carta debe presentarse en papel membretado y con el sello oficial de la empresa o institución.
                                </v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </div>

                        <div class="mb-4">
                          <h4 class="text-h6 font-weight-bold mb-3 cyan--text mono-font">
                            <v-icon left color="cyan">mdi-email-send-outline</v-icon>
                            Envío de Documentos
                          </h4>
                          <p class="white--text body-1 mb-3">
                            Una vez completados, envíe la plantilla de Registro de Dominio y la carta de compromiso al correo oficial.
                          </p>
                          <v-card outlined color="rgba(0,0,0,0.5)" class="border-blue pa-4 mb-3">
                            <div class="d-flex align-center">
                              <v-icon class="mr-3" color="blue accent-3">mdi-email</v-icon>
                              <div>
                                <p class="grey--text body-2 mb-1">Correo para envío de documentos:</p>
                                <a 
                                  href="mailto:info@nic.uni.edu.ni" 
                                  class="text-h6 blue--text text--accent-3 text-decoration-none font-weight-bold tech-link"
                                >
                                  info@nic.uni.edu.ni
                                </a>
                              </div>
                            </div>
                          </v-card>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <v-tab-item value="tab-3">
              <v-card flat color="transparent">
                <v-card-text class="pa-8">
                  <div class="text-center mb-8">
                    <v-icon size="80" color="cyan accent-3" class="mb-4 glow-icon">mdi-folder-multiple-outline</v-icon>
                    <h2 class="text-h4 white--text font-weight-bold neon-text">Remita Documentación de Respaldo</h2>
                    <p class="cyan--text text--lighten-2 mono-font">>> Documentación adicional requerida</p>
                  </div>

                  <v-row>
                    <v-col cols="12" md="10" class="mx-auto">
                      <div class="console-box pa-6 rounded border-cyan mb-6">
                        <div class="mb-6">
                          <h4 class="text-h6 font-weight-bold mb-3 cyan--text mono-font">
                            <v-icon left color="cyan">mdi-card-account-details-outline</v-icon>
                            Documentación Básica Obligatoria
                          </h4>
                          <div class="d-flex flex-wrap justify-center mb-4">
                            <v-chip outlined color="red accent-2" class="ma-2 neon-chip" large>
                              <v-avatar left>
                                <v-icon color="red accent-2">mdi-account</v-icon>
                              </v-avatar>
                              <strong class="white--text">Cédula de Identidad</strong>&nbsp;<span class="grey--text">(Persona natural)</span>
                            </v-chip>
                            <v-chip outlined color="red accent-2" class="ma-2 neon-chip" large>
                              <v-avatar left>
                                <v-icon color="red accent-2">mdi-office-building</v-icon>
                              </v-avatar>
                              <strong class="white--text">RUC</strong>&nbsp;<span class="grey--text">(Empresas)</span>
                            </v-chip>
                          </div>
                        </div>

                        <div class="mb-6">
                          <h4 class="text-h6 font-weight-bold mb-3 cyan--text mono-font">
                            <v-icon left color="cyan">mdi-folder-information-outline</v-icon>
                            Documentación Específica por Tipo
                          </h4>
                          
                          <v-tabs v-model="docTab" background-color="transparent" dark grow slider-color="cyan accent-3">
                            <v-tab class="white--text"><v-icon left small>mdi-cards-playing-outline</v-icon> Casinos</v-tab>
                            <v-tab class="white--text"><v-icon left small>mdi-school</v-icon> .edu</v-tab>
                            <v-tab class="white--text"><v-icon left small>mdi-account-group</v-icon> .org</v-tab>
                          </v-tabs>

                          <v-tabs-items v-model="docTab" class="transparent mt-4">
                            <v-tab-item>
                              <v-card flat color="rgba(255,255,255,0.05)" class="pa-4 border-cyan">
                                <h5 class="text-h6 font-weight-bold mb-3 white--text">Actividad económica: casino o similar</h5>
                                <v-list color="transparent" dark>
                                  <v-list-item>
                                    <v-list-item-icon><v-icon color="green accent-3">mdi-check</v-icon></v-list-item-icon>
                                    <v-list-item-content>
                                      <v-list-item-title class="font-weight-bold cyan--text">Título o Licencia de Operación</v-list-item-title>
                                      <v-list-item-subtitle class="grey--text">Debe estar vigente y legible</v-list-item-subtitle>
                                    </v-list-item-content>
                                  </v-list-item>
                                </v-list>
                              </v-card>
                            </v-tab-item>
                            <v-tab-item>
                              <v-card flat color="rgba(255,255,255,0.05)" class="pa-4 border-cyan">
                                <h5 class="text-h6 font-weight-bold mb-3 white--text">Zona .edu</h5>
                                <v-list color="transparent" dark>
                                  <v-list-item>
                                    <v-list-item-icon><v-icon color="green accent-3">mdi-check</v-icon></v-list-item-icon>
                                    <v-list-item-content>
                                      <v-list-item-title class="font-weight-bold cyan--text">Constancia emitida por MINED, INATEC o SETEC</v-list-item-title>
                                      <v-list-item-subtitle class="grey--text">Según el tipo de institución educativa</v-list-item-subtitle>
                                    </v-list-item-content>
                                  </v-list-item>
                                </v-list>
                              </v-card>
                            </v-tab-item>
                            <v-tab-item>
                              <v-card flat color="rgba(255,255,255,0.05)" class="pa-4 border-cyan">
                                <h5 class="text-h6 font-weight-bold mb-3 white--text">Zona .org</h5>
                                <v-list color="transparent" dark>
                                  <v-list-item>
                                    <v-list-item-icon><v-icon color="green accent-3">mdi-check</v-icon></v-list-item-icon>
                                    <v-list-item-content>
                                      <v-list-item-title class="font-weight-bold cyan--text">Resolución Administrativa de autorización</v-list-item-title>
                                      <v-list-item-subtitle class="grey--text">Para registro de la OSFL y Número Perpetuo</v-list-item-subtitle>
                                    </v-list-item-content>
                                  </v-list-item>
                                </v-list>
                              </v-card>
                            </v-tab-item>
                          </v-tabs-items>
                        </div>

                        <v-alert border="left" colored-border color="red accent-2" class="mb-4 black-glass">
                          <div class="d-flex align-start">
                            <v-icon left color="red accent-2">mdi-alert-circle-outline</v-icon>
                            <div class="white--text">
                              <strong>Importante:</strong> La falta de remisión de la documentación requerida impedirá 
                              el Registro del nuevo dominio.
                            </div>
                          </div>
                        </v-alert>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <v-tab-item value="tab-4">
              <v-card flat color="transparent">
                <v-card-text class="pa-8">
                  <div class="text-center mb-8">
                    <v-icon size="80" color="green accent-3" class="mb-4 glow-icon">mdi-cash-multiple</v-icon>
                    <h2 class="text-h4 white--text font-weight-bold neon-text">Efectuar Pago</h2>
                    <p class="cyan--text text--lighten-2 mono-font">>> Tarifas y métodos de pago</p>
                  </div>

                  <v-row>
                    <v-col cols="12" md="10" class="mx-auto">
                      <div class="console-box pa-6 rounded border-cyan mb-6">
                        <div class="mb-6">
                          <v-alert border="left" colored-border color="blue accent-3" class="mb-4 black-glass">
                            <div class="d-flex align-center">
                              <v-icon left color="blue accent-3">mdi-information-outline</v-icon>
                              <div class="white--text">
                                <strong>Nota:</strong> Previo a efectuar el pago, deberá presentarse en las oficinas 
                                de atención al cliente de NIC.NI, a fin de gestionar la emisión de la orden de pago 
                                correspondiente.
                              </div>
                            </div>
                          </v-alert>

                          <h4 class="text-h6 font-weight-bold mb-4 cyan--text mono-font">
                            <v-icon left color="cyan">mdi-cash</v-icon>
                            Tarifas Vigentes
                          </h4>
                          
                          <v-row class="mb-6">
                            <v-col cols="12" md="6">
                              <v-card class="text-center pa-4" outlined color="rgba(255,255,255,0.05)" style="border: 1px solid cyan">
                                <v-icon size="60" class="mb-3" color="cyan accent-3">mdi-web</v-icon>
                                <h5 class="text-h4 font-weight-bold white--text">C$ 18,500.00</h5>
                                <p class="text-h6 font-weight-bold mb-2 cyan--text">Dominios de II Nivel</p>
                                <p class="caption grey--text">ejemplo.ni</p>
                              </v-card>
                            </v-col>
                            <v-col cols="12" md="6">
                              <v-card class="text-center pa-4" outlined color="rgba(255,255,255,0.05)" style="border: 1px solid #4A81D4">
                                <v-icon size="60" class="mb-3" color="blue accent-3">mdi-web-box</v-icon>
                                <h5 class="text-h4 font-weight-bold white--text">C$ 1,850.00</h5>
                                <p class="text-h6 font-weight-bold mb-2 blue--text text--accent-3">Dominios de III Nivel</p>
                                <p class="caption grey--text">ejemplo.com.ni</p>
                              </v-card>
                            </v-col>
                          </v-row>
                        </div>

                        <div class="mb-6">
                          <h4 class="text-h6 font-weight-bold mb-4 cyan--text mono-font">
                            <v-icon left color="cyan">mdi-map-marker-radius</v-icon>
                            Punto de Pago
                          </h4>
                          
                          <v-card outlined color="rgba(0,0,0,0.5)" class="border-blue pa-4 mb-4">
                            <div class="d-flex align-start">
                              <v-icon class="mr-3 mt-1" color="blue accent-3">mdi-bank-outline</v-icon>
                              <div>
                                <h6 class="text-subtitle-1 font-weight-bold mb-2 white--text">Caja General de la UNI</h6>
                                <p class="body-1 mb-1 grey--text">
                                  <v-icon small left color="blue accent-3">mdi-map-marker</v-icon>
                                  Recinto Universitario Simón Bolívar
                                </p>
                                <p class="body-1 mb-1 grey--text">
                                  <v-icon small left color="blue accent-3">mdi-road</v-icon>
                                  Avenida Universitaria, frente a la Escuela de Danza
                                </p>
                                <p class="body-1 mb-2 grey--text">
                                  <v-icon small left color="blue accent-3">mdi-city</v-icon>
                                  Managua, Nicaragua
                                </p>
                                <v-chip outlined color="green accent-3" class="mt-2 neon-chip">
                                  <v-icon left>mdi-cash-multiple</v-icon>
                                  Solo efectivo o cheques certificados
                                </v-chip>
                              </div>
                            </div>
                          </v-card>
                        </div>

                        <div class="mt-6 text-center">
                          <v-alert border="left" colored-border color="orange accent-3" class="d-inline-block black-glass">
                            <div class="d-flex align-center">
                              <v-icon left color="orange accent-3">mdi-clock-alert-outline</v-icon>
                              <div class="white--text">
                                <strong>Horario de atención:</strong> Lunes a Viernes de 8:00 AM a 4:00 PM
                              </div>
                            </div>
                          </v-alert>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <v-tab-item value="tab-5">
              <v-card flat color="transparent">
                <v-card-text class="pa-8">
                  <div class="text-center mb-8">
                    <v-icon size="80" color="green accent-3" class="mb-4 glow-icon">mdi-receipt-text-check-outline</v-icon>
                    <h2 class="text-h4 white--text font-weight-bold neon-text">Emisión ROC</h2>
                    <p class="cyan--text text--lighten-2 mono-font">>> Finalización del proceso</p>
                  </div>

                  <v-row>
                    <v-col cols="12" md="8" class="mx-auto">
                      <div class="console-box pa-6 rounded border-green mb-6">
                        <div class="text-center mb-6">
                          <v-avatar size="120" class="mb-4 neon-border-circle">
                            <v-icon size="60" color="green accent-3">mdi-check-decagram</v-icon>
                          </v-avatar>
                          <h3 class="text-h5 font-weight-bold mb-3 white--text">Proceso Finalizado</h3>
                          <p class="body-1 grey--text">
                            Una vez efectuado el pago, se generará y entregará el Recibo Oficial de Caja (ROC).
                          </p>
                        </div>

                        <v-list color="transparent" dark class="mb-6">
                          <v-list-item>
                            <v-list-item-icon>
                              <v-icon size="large" color="cyan accent-3">mdi-receipt</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-h6 font-weight-bold white--text">Recibo Oficial de Caja (ROC)</v-list-item-title>
                              <v-list-item-subtitle class="grey--text">
                                Se genera automáticamente al completar el pago. Guarde este comprobante.
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          <v-divider class="grey darken-3 my-3"></v-divider>
                          <v-list-item>
                            <v-list-item-icon>
                              <v-icon size="large" color="cyan accent-3">mdi-domain</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-h6 font-weight-bold white--text">Registro Completado</v-list-item-title>
                              <v-list-item-subtitle class="grey--text">
                                Con el ROC emitido, se procederá a completar el registro del dominio.
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          <v-divider class="grey darken-3 my-3"></v-divider>
                          <v-list-item>
                            <v-list-item-icon>
                              <v-icon size="large" color="cyan accent-3">mdi-email-check</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-h6 font-weight-bold white--text">Notificación por Correo</v-list-item-title>
                              <v-list-item-subtitle class="grey--text">
                                Recibirá confirmación en el correo registrado.
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>

                        <v-alert border="left" colored-border color="green accent-3" class="mb-4 black-glass">
                          <div class="d-flex align-center">
                            <v-icon left color="green accent-3">mdi-check-circle-outline</v-icon>
                            <div class="white--text">
                              <strong>Proceso completado:</strong> Su dominio estará registrado y activo.
                            </div>
                          </div>
                        </v-alert>

                        <div class="text-center mt-6">
                          <h4 class="text-h6 font-weight-bold mb-3 white--text">¿Necesita ayuda?</h4>
                          <v-btn outlined color="cyan accent-3" class="mr-3 neon-btn" :href="`mailto:${contactEmail}`">
                            <v-icon left>mdi-email</v-icon>
                            Contactar Soporte
                          </v-btn>
                          <v-btn color="cyan accent-4" class="black--text font-weight-bold neon-bg" href="https://www.nic.ni" target="_blank">
                            <v-icon left color="black">mdi-web</v-icon>
                            Visitar Sitio Web
                          </v-btn>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs-items>

          <v-divider class="neon-divider"></v-divider>
          <v-card-actions class="pa-6 bg-black-transparent">
            <v-spacer></v-spacer>
            <v-btn 
              text
              color="grey lighten-1"
              class="mr-4"
              :disabled="currentStep === 'tab-0'" 
              @click="previousStep"
            >
              <v-icon left>mdi-chevron-left</v-icon>
              Anterior
            </v-btn>
            <v-btn 
              color="cyan accent-3"
              class="black--text font-weight-bold neon-bg px-6"
              :disabled="currentStep === 'tab-5'" 
              @click="nextStep"
            >
              Siguiente
              <v-icon right color="black">mdi-chevron-right</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>

        <v-card class="mt-8 tech-card border-blue-dim" elevation="5">
          <v-card-text class="pa-6">
            <v-row class="text-center">
              <v-col cols="12" md="3" class="mb-4 mb-md-0">
                <v-icon class="mb-2" color="blue accent-3">mdi-phone</v-icon>
                <p class="body-2 font-weight-bold mb-1 white--text mono-font">Teléfonos</p>
                <p class="caption grey--text">(505) 2278-3141</p>
                <p class="caption grey--text">(505) 2270-0858</p>
              </v-col>
              <v-col cols="12" md="3" class="mb-4 mb-md-0">
                <v-icon class="mb-2" color="blue accent-3">mdi-map-marker</v-icon>
                <p class="body-2 font-weight-bold mb-1 white--text mono-font">Dirección</p>
                <p class="caption grey--text">Universidad Nacional de Ingeniería</p>
                <p class="caption grey--text">Recinto Simón Bolívar</p>
              </v-col>
              <v-col cols="12" md="3" class="mb-4 mb-md-0">
                <v-icon class="mb-2" color="blue accent-3">mdi-web</v-icon>
                <p class="body-2 font-weight-bold mb-1 white--text mono-font">Sitio Web</p>
                <a href="https://www.nic.ni" target="_blank" class="caption cyan--text text-decoration-none font-weight-bold tech-link">
                  www.nic.ni
                </a>
              </v-col>
              <v-col cols="12" md="3">
                <v-icon class="mb-2" color="blue accent-3">mdi-email</v-icon>
                <p class="body-2 font-weight-bold mb-1 white--text mono-font">Correo</p>
                <a href="mailto:info@nic.uni.edu.ni" class="caption cyan--text text-decoration-none font-weight-bold tech-link">
                  info@nic.uni.edu.ni
                </a>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-container>
    </section>
  </v-app>
</template>

<script>
export default {
  name: "RegistroNuevoDominioTech",
  data() {
    return {
      currentStep: 'tab-0',
      docTab: null,
      steps: [
        { 
          icon: 'mdi-magnify', 
          title: 'Verificar Disponibilidad',
          shortTitle: 'Disponibilidad'
        },
        { 
          icon: 'mdi-file-download', 
          title: 'Descargar Documentos',
          shortTitle: 'Documentos'
        },
        { 
          icon: 'mdi-file-edit', 
          title: 'Completar Documentos',
          shortTitle: 'Completar'
        },
        { 
          icon: 'mdi-folder-multiple', 
          title: 'Remitir Documentación',
          shortTitle: 'Documentación'
        },
        { 
          icon: 'mdi-cash-multiple', 
          title: 'Efectuar Pago',
          shortTitle: 'Pago'
        },
        { 
          icon: 'mdi-receipt', 
          title: 'Emisión ROC',
          shortTitle: 'ROC'
        }
      ],
      contactEmail: 'info@nic.uni.edu.ni'
    };
  },
  computed: {
    currentStepIndex() {
      return parseInt(this.currentStep.replace('tab-', ''));
    },
    progressWidth() {
      if(this.steps.length <= 1) return '0%';
      const progress = (this.currentStepIndex / (this.steps.length - 1)) * 100;
      return `${progress}%`;
    }
  },
  methods: {
    nextStep() {
      const currentIndex = parseInt(this.currentStep.replace('tab-', ''));
      if (currentIndex < this.steps.length - 1) {
        this.currentStep = `tab-${currentIndex + 1}`;
      }
    },
    previousStep() {
      const currentIndex = parseInt(this.currentStep.replace('tab-', ''));
      if (currentIndex > 0) {
        this.currentStep = `tab-${currentIndex - 1}`;
      }
    },
    goToStep(index) {
      this.currentStep = `tab-${index}`;
    }
  }
};
</script>

<style scoped>
/* --- ESTILOS CYBERPUNK / HIGH-TECH --- */
@import url('https://fonts.googleapis.com/css2?family=Fira+Code:wght@400;600&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&display=swap');

.tech-app-wrapper {
  background-color: #050a14 !important;
  font-family: 'Montserrat', sans-serif;
  min-height: 100vh;
}

.mono-font { font-family: 'Fira Code', monospace !important; }
.tracking-widest { letter-spacing: 3px; }

/* Fondos */
.circuit-overlay {
  position: absolute; top: 0; left: 0; width: 100%; height: 100%;
  background-image: radial-gradient(circle at 50% 0%, rgba(0, 229, 255, 0.1) 0%, transparent 60%);
  z-index: 0;
}
.scanlines {
  position: absolute; top: 0; left: 0; width: 100%; height: 100%;
  background: linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0) 50%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.1));
  background-size: 100% 4px;
  pointer-events: none; z-index: 1;
}
.content-relative { position: relative; z-index: 2; }

/* Hero Tech */
.hero-section-tech {
  background-image: linear-gradient(to bottom, rgba(5,10,20,0.85), #050a14), url('~@/assets/img/tiscapa.jpg');
  background-size: cover;
  background-position: center;
  min-height: 400px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid rgba(0, 229, 255, 0.2);
}
.scan-line-hero {
  width: 100px; height: 2px; background: cyan; margin: 20px auto;
  box-shadow: 0 0 10px cyan;
}

/* Tarjeta Principal */
.tech-card {
  background: rgba(13, 25, 45, 0.95) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 229, 255, 0.2) !important;
}

/* Timeline / Stepper Tech */
.progress-tech-container { position: relative; overflow: hidden; }
.progress-line-base {
  position: absolute; top: 22px; left: 0; right: 0; height: 2px; background: rgba(255,255,255,0.1); z-index: 0;
}
.progress-line-active {
  position: absolute; top: 22px; left: 0; height: 2px; background: #00E5FF;
  box-shadow: 0 0 10px #00E5FF; transition: width 0.5s ease; z-index: 1;
}
.step-tech-node {
  position: relative; z-index: 2; transition: all 0.3s;
}
.node-circle {
  width: 45px; height: 45px; border-radius: 50%; background: #050a14; border: 2px solid rgba(255,255,255,0.3);
  display: flex; align-items: center; justify-content: center; font-weight: bold; color: white;
  transition: all 0.3s;
  box-shadow: 0 0 10px rgba(0,0,0,0.5);
}
.step-tech-node.active .node-circle {
  background: #00E5FF; color: black; border-color: #00E5FF; box-shadow: 0 0 15px #00E5FF;
}
.step-tech-node.current .node-circle {
  transform: scale(1.1); border-color: white; box-shadow: 0 0 20px cyan;
}
.step-tech-node.active .node-label { color: #00E5FF; text-shadow: 0 0 5px cyan; }
.node-label { color: grey; transition: all 0.3s; margin-top: 5px; }

/* Elementos Visuales */
.neon-text { text-shadow: 0 0 10px rgba(0, 229, 255, 0.6); }
.glow-icon { filter: drop-shadow(0 0 8px cyan); }
.console-box {
  background: rgba(0,0,0,0.4);
  border: 1px solid rgba(0, 229, 255, 0.2);
  border-left: 3px solid #00E5FF;
}
.neon-btn { border: 1px solid currentColor; transition: all 0.3s; }
.glow-on-hover:hover { box-shadow: 0 0 20px cyan; background: rgba(0, 229, 255, 0.1); }
.neon-bg {
  background: linear-gradient(90deg, #00e5ff, #00b8d4) !important;
  box-shadow: 0 0 15px rgba(0, 229, 255, 0.4);
}
.tech-link { color: #00E5FF !important; text-decoration: none; border-bottom: 1px dashed #00E5FF; }
.tech-link:hover { border-bottom-style: solid; text-shadow: 0 0 8px cyan; }

/* Bordes y Colores */
.border-cyan { border: 1px solid #00E5FF !important; }
.border-blue { border: 1px solid #2979FF !important; }
.border-green { border: 1px solid #00E676 !important; }
.border-bottom-neon { border-bottom: 1px solid rgba(0, 229, 255, 0.2); }
.neon-divider { border-color: rgba(0, 229, 255, 0.2) !important; }
.bg-black-transparent { background: rgba(0,0,0,0.3); }
.black-glass { background-color: rgba(0,0,0,0.6) !important; }
.neon-chip { border-color: currentColor !important; box-shadow: 0 0 5px currentColor; background: transparent !important; }
.neon-border-circle { border: 2px solid cyan; box-shadow: 0 0 10px rgba(0,255,255,0.3); }

/* Animaciones */
.spin-slow-reverse { animation: spinRev 15s linear infinite; }
@keyframes spinRev { 100% { transform: rotate(-360deg); } }
.custom-scrollbar::-webkit-scrollbar { height: 6px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #00E5FF; border-radius: 3px; }
</style>